import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getRouteSeo, metaEntries, serializeSchema } from '../../lib/seo/siteSeo';

const SeoContext = createContext(null);

function applyMetadata(metadata) {
  document.title = metadata.title;
  for (const [attribute, key, content] of metaEntries(metadata)) {
    let tag = document.head.querySelector('meta[' + attribute + '="' + key + '"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(attribute, key);
      document.head.appendChild(tag);
    }
    tag.content = content;
  }

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = metadata.canonical;

  let schema = document.getElementById('site-schema');
  if (metadata.schema) {
    if (!schema) {
      schema = document.createElement('script');
      schema.id = 'site-schema';
      schema.type = 'application/ld+json';
      document.head.appendChild(schema);
    }
    schema.textContent = serializeSchema(metadata.schema);
  } else {
    schema?.remove();
  }
}

/** One head manager owns both initial tags and client-side route updates. */
export function SeoProvider({ children }) {
  const { pathname } = useLocation();
  const [override, setOverride] = useState(null);
  const metadata = useMemo(() => ({
    ...getRouteSeo(pathname),
    ...(override?.pathname === pathname ? override.metadata : {}),
  }), [pathname, override]);

  useEffect(() => {
    applyMetadata(metadata);
  }, [metadata]);

  return <SeoContext.Provider value={setOverride}>{children}</SeoContext.Provider>;
}

/** Product details arrive asynchronously; tie overrides to the current URL. */
export function usePageSeo(metadata) {
  const setOverride = useContext(SeoContext);
  const { pathname } = useLocation();
  useEffect(() => {
    if (!setOverride) return undefined;
    setOverride({ pathname, metadata });
    return () => setOverride(null);
  }, [pathname, metadata, setOverride]);
}
